import React from "react";
import { pointsCreator } from "../../../EditIgnoreZones/EditIgnoreZonesUtils/pointsCreatorLarger";
import { CameraContext, FlexCameraSummary, FlexPreset } from "./types";

export const FlexIgnoreZonesCameraPresetCard = ({
  scheduleId,
  camera,
  preset,
  cameraContext,
  maxPreviewWidth = 236,
  presetLineReplacement,
  onPreviewImageError,
}: {
  scheduleId: number;
  camera: FlexCameraSummary;
  preset: FlexPreset;
  cameraContext?: CameraContext;
  /** Cap preview width so ~3 cards fit per row next to the schedule grid (~1500px). */
  maxPreviewWidth?: number;
  /** When set, replaces the static "Preset: …" row (e.g. edit-mode preset dropdown). */
  presetLineReplacement?: React.ReactNode;
  /** Optional callback fired if preview image fails to load. */
  onPreviewImageError?: () => void;
}) => {
  const previewUrl =
    cameraContext?.preview?.previewUrl || camera.preview_url || "";
  const dimensions = cameraContext?.preview?.adjustedDimensions || {
    x: 720,
    y: 405,
  };
  const scale = Math.min(1, maxPreviewWidth / dimensions.x);
  const displayWidth = Math.round(dimensions.x * scale);
  const displayHeight = Math.round(dimensions.y * scale);
  const zoneStroke = displayWidth < 300 ? 2 : 3;
  const [previewLoadFailed, setPreviewLoadFailed] = React.useState(false);

  React.useEffect(() => {
    setPreviewLoadFailed(false);
  }, [previewUrl]);

  const displayedZones = React.useMemo(
    () =>
      (preset.zones || []).filter((zone) => (zone.coordinates?.length ?? 0) > 0),
    [preset.zones]
  );

  return (
    <div className="w-fit max-w-full border border-gray-200 bg-gray-100 rounded-[6px] p-[8px]">
      <p className="font-mulish text-[13px] font-black leading-tight">
        {camera.camera_name}
      </p>
      {presetLineReplacement != null ? (
        <div className="w-full min-w-0 pt-[12px] pb-[14px]">{presetLineReplacement}</div>
      ) : (
        <p className="font-mulish text-[12px] pb-[6px] leading-snug">
          <span className="font-black">Preset:</span> {preset.name}
        </p>
      )}
      <div className="flex items-start gap-[8px] flex-wrap w-fit max-w-full">
        {previewUrl && !previewLoadFailed ? (
          <div className="relative inline-block border border-gray-200 rounded-[4px] overflow-hidden">
            <img
              src={previewUrl}
              alt={camera.camera_name}
              onError={() => {
                setPreviewLoadFailed(true);
                onPreviewImageError?.();
              }}
              style={{
                width: `${displayWidth}px`,
                height: `${displayHeight}px`,
              }}
            />
            {displayedZones.map((zone, zIdx) => (
              <div
                key={`${scheduleId}-${camera.id}-${zIdx}-${zone.metric_id}-${zone.preset_zone_id ?? zone.label}`}
                className="absolute top-0 left-0"
              >
                {zone.coordinates.map((coordinates, index) => (
                  <svg
                    key={`${zone.label}-${index}`}
                    style={{
                      width: `${displayWidth}px`,
                      height: `${displayHeight}px`,
                    }}
                    className="absolute top-0 left-0"
                  >
                    <polygon
                      points={pointsCreator(
                        coordinates,
                        displayHeight,
                        displayWidth
                      )}
                      stroke={zone.line_color}
                      strokeWidth={zoneStroke}
                      fill={zone.line_color}
                      fillOpacity="50%"
                    />
                  </svg>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <p className="font-mulish text-[13px] text-gray-600">
            Camera preview unavailable.
          </p>
        )}
        {displayedZones.length > 0 && (
          <div className="shrink-0 w-max max-w-[min(132px,100%)]">
            <p className="font-mulish text-[11px] font-black pb-[4px]">Preset zones</p>
            {displayedZones.map((zone, zIdx) => (
              <div
                key={`${scheduleId}-${camera.id}-legend-${zIdx}-${zone.metric_id}-${zone.preset_zone_id ?? zone.label}`}
                className="flex items-center gap-[5px] pb-[3px]"
              >
                <span
                  style={{
                    background: zone.line_color,
                    width: "4px",
                    height: "14px",
                    display: "inline-block",
                  }}
                />
                <p className="font-mulish text-[11px] leading-[13px]">
                  {zone.label_display_name}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
